import React from 'react'
import { useCanvasContextMenu } from '../../stores/canvasContextMenu'
import { Clipboard } from '../../core/clipboard/Clipboard'
import { getClipboardContentMin, hasClipboardContent } from '../../core/clipboard/clipboardSelection'
import { PasteNodesCommand } from '../../core/commands/PasteNodesCommand'
import { getCommandManager } from '../../hooks/useCommandManager'
import { MessageCirclePlus, ClipboardPaste, Copy as CopyIcon, Paintbrush, Save, GitBranch, ExternalLink, Unlink, BringToFront, MoveUp, MoveDown, SendToBack, Link2, LayoutGrid, Check } from 'lucide-react'
import { useDiagramStore } from '../../stores/diagramStore'
import { CopyNodeCommand } from '../../core/commands/CopyNodeCommand'
import { ChangeNodeStyleCommand } from '../../core/commands/ChangeNodeStyleCommand'
import { useTranslation } from '../../hooks/useTranslation'
import { SaveTableTemplateDialog } from '../tables/SaveTableTemplateDialog'
import { extractSchemaFromTableNode, saveCustomSchema, getCustomSchema } from '../../domains/diagram/shapes/table/customTableSchemas'
import type { CustomTableSchema } from '../../domains/diagram/shapes/table/customTableSchemas'
import { LinkDiagramCommand } from '../../core/commands/LinkDiagramCommand'
import { UnlinkDiagramCommand } from '../../core/commands/UnlinkDiagramCommand'
import { AdjustZIndexCommand } from '../../core/commands/AdjustZIndexCommand'
import { getCookie } from '../../lib/cookies'
import { useDiagramDocument } from '../../contexts/DiagramDocumentContext'
import { useOrgChartViewStore } from '../../stores/orgchartViewStore'
import { useDiagramConnections } from '../../domains/data-binding/hooks/useDiagramConnections'
import { getProfileForDiagramType } from '../../core/data-profiles/profileRegistry'
import { addBindingToShape } from '../../domains/data-binding/utils/addBindingToShape'

// Shape variants that support sub-process creation
const SUBPROCESS_VARIANTS = new Set([
  'process', 'predefined-process', 'subprocess', 'sub-process',
  'manual-operation', 'preparation', 'internal-storage', 'stored-data',
  'task', 'call-activity', 'transaction', 'predefined'
])

const isProcessShape = (node: any): boolean => {
  if (!node) return false
  const type = node.type || ''
  if (type !== 'flowchart-shape') return false
  const attrs = node.attrs || {}
  const variant = attrs.variant || attrs.shapeType || ''
  return SUBPROCESS_VARIANTS.has(variant)
}

export const CanvasContextMenu: React.FC = () => {
  const { t } = useTranslation('common')
  const menu = useCanvasContextMenu()
  const ref = React.useRef<HTMLDivElement | null>(null)
  const [dims, setDims] = React.useState<{ w: number; h: number }>({ w: 0, h: 0 })
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = React.useState(false)
  const [tableShapeIdForTemplate, setTableShapeIdForTemplate] = React.useState<string | null>(null)
  const [schemaToExport, setSchemaToExport] = React.useState<CustomTableSchema | null>(null)
  const [isCreatingSubProcess, setIsCreatingSubProcess] = React.useState(false)
  const { diagramType, doc } = useDiagramDocument()
  const autoLayoutEnabled = useOrgChartViewStore(s => s.autoLayoutEnabled)
  const setAutoLayoutEnabled = useOrgChartViewStore(s => s.setAutoLayoutEnabled)

  // Get diagram-level data source connection (used as fallback for sub-process linking)
  const currentDiagramId = React.useMemo(() => {
    const globalObj = window as unknown as { __DIAGRAM_ID__?: string }
    return globalObj.__DIAGRAM_ID__ ?? ''
  }, [])
  const { data: connectionsData } = useDiagramConnections(currentDiagramId || undefined)
  const diagramSourceId = connectionsData?.connections?.[0]?.dataSourceId

  // Close on outside interactions for good UX
  React.useEffect(() => {
    if (!menu.open) return
    const onGlobalClick = () => menu.close()
    const onScroll = () => menu.close()
    const onResize = () => menu.close()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') menu.close() }
    window.addEventListener('click', onGlobalClick)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('click', onGlobalClick)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('keydown', onKey)
    }
  }, [menu])

  React.useLayoutEffect(() => {
    if (!menu.open) return
    const el = ref.current
    if (!el) return
    setDims({ w: el.offsetWidth, h: el.offsetHeight })
  }, [menu.open])

  if (!menu.open) return null

  const margin = 8
  const vw = typeof window !== 'undefined' ? window.innerWidth : 0
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0
  const clampedX = Math.max(margin, Math.min(menu.screenX, Math.max(margin, vw - (dims.w || 0) - margin)))
  const clampedY = Math.max(margin, Math.min(menu.screenY, Math.max(margin, vh - (dims.h || 0) - margin)))
  const style: React.CSSProperties = { position: 'fixed', top: clampedY, left: clampedX, zIndex: 50 }

  const onPaste = async () => {
    try {
      let data = Clipboard.get()
      try {
        const sys = await Clipboard.readFromSystem()
        if (sys && hasClipboardContent(sys)) data = sys
      } catch {}
      if (!hasClipboardContent(data)) {
        const win = window as unknown as {
          __pasteCanvasImageFromClipboard?: (opts?: { diagramX: number; diagramY: number }) => Promise<boolean>;
        };
        try {
          await win.__pasteCanvasImageFromClipboard?.({
            diagramX: menu.diagramX,
            diagramY: menu.diagramY,
          });
        } catch {
          /* clipboard API may throw (permissions / unsupported) */
        }
        menu.close();
        return;
      }

      // Compute offset so the clipboard bounding box top-left aligns to pointer diagram coords.
      const min = getClipboardContentMin(data) ?? { x: menu.diagramX, y: menu.diagramY }
      const minX = min.x
      const minY = min.y
      const offset = { x: menu.diagramX - minX, y: menu.diagramY - minY }

      const cmd = new PasteNodesCommand(data, offset)
      const cm = getCommandManager()
      await cm.execute(cmd)
    } finally {
      menu.close()
    }
  }

  // In-memory style clipboard (page scoped) – shared with ShapeToolbar
  const styleClipboard: { styles?: Record<string, any> } = (window as any).__STYLE_CLIPBOARD__ || ((window as any).__STYLE_CLIPBOARD__ = {})

  // Collect normalized styles from a shape (fill, outline, text)
  const collectStyles = (shape: any): Record<string, any> => {
    const attrs = (shape?.attrs || {}) as Record<string, any>
    return {
      fillColor: shape.fillColor ?? attrs.fillColor,
      strokeColor: shape.strokeColor ?? attrs.strokeColor,
      strokeWidth: shape.strokeWidth ?? attrs.strokeWidth ?? (attrs as any).borderWidth,
      dash: Array.isArray(attrs.dash) ? attrs.dash : (Array.isArray((attrs as any).borderDash) ? (attrs as any).borderDash : undefined),
      dashEnabled: typeof attrs.dashEnabled === 'boolean' ? attrs.dashEnabled : undefined,
      fontFamily: attrs.fontFamily ?? shape.fontFamily,
      fontSize: attrs.fontSize ?? shape.fontSize,
      fontWeight: attrs.fontWeight,
      fontStyle: attrs.fontStyle,
      textDecoration: attrs.textDecoration,
      textAlign: attrs.textAlign,
      verticalAlign: attrs.verticalAlign,
      textColor: attrs.textColor,
    }
  }

  const onCopy = async () => {
    try {
      const store = useDiagramStore.getState()
      const selectedIds = store.selectedIds
      const ids = selectedIds.size > 1
        ? Array.from(selectedIds)
        : (menu.target && menu.target.kind === 'shape' ? [menu.target.shapeId] : [])
      if (!ids.length) { menu.close(); return }
      const cm = getCommandManager()
      await cm.execute(new CopyNodeCommand(ids))
    } finally {
      menu.close()
    }
  }

  const onCopyStyle = async () => {
    try {
      if (!menu.target || menu.target.kind !== 'shape') { menu.close(); return }
      const targetShapeId = menu.target.shapeId
      const store = useDiagramStore.getState()
      const node = store.nodes.find(n => n.id === targetShapeId)
      if (!node) { menu.close(); return }
      const styles = collectStyles(node)
      styleClipboard.styles = styles
      try { await navigator.clipboard?.writeText?.(JSON.stringify({ type: 'creately-style', styles })) } catch {}
    } finally {
      menu.close()
    }
  }

  const onPasteStyle = async () => {
    try {
      if (!menu.target || menu.target.kind !== 'shape') { menu.close(); return }
      const targetShapeId = menu.target.shapeId
      const store = useDiagramStore.getState()
      const selectedIds = store.selectedIds
      const targetIds = selectedIds.size > 1 ? Array.from(selectedIds) : [targetShapeId]
      if (targetIds.length === 0) { menu.close(); return }
      let styles = styleClipboard.styles
      if (!styles) {
        try {
          const text = await navigator.clipboard?.readText?.()
          const parsed = JSON.parse(text || '{}')
          if (parsed && parsed.type === 'creately-style') styles = parsed.styles as Record<string, any>
        } catch {}
      }
      if (!styles) { menu.close(); return }
      const cm = getCommandManager()
      const apply = async (key: string, value: any) => {
        if (value === undefined) return
        try { await cm.execute(new ChangeNodeStyleCommand(targetIds, key, value)) } catch {}
      }
      await apply('fillColor', (styles as any).fillColor)
      await apply('strokeColor', (styles as any).strokeColor)
      await apply('strokeWidth', (styles as any).strokeWidth)
      await apply('dash', (styles as any).dash)
      await apply('dashEnabled', (styles as any).dashEnabled)
      await apply('fontFamily', (styles as any).fontFamily)
      await apply('fontSize', (styles as any).fontSize)
      await apply('fontWeight', (styles as any).fontWeight)
      await apply('fontStyle', (styles as any).fontStyle)
      await apply('textDecoration', (styles as any).textDecoration)
      await apply('textAlign', (styles as any).textAlign)
      await apply('verticalAlign', (styles as any).verticalAlign)
      await apply('textColor', (styles as any).textColor)
    } finally {
      menu.close()
    }
  }

  const onAddComment = () => {
    // Emit event understood by DiagramCanvasWithTransform bridge
    const evt = new CustomEvent('canvas-action', { detail: { action: 'add-comment' } })
    window.dispatchEvent(evt)
    menu.close()
  }

  // Get current diagram ID and folder ID from URL
  const getCurrentDiagramInfo = () => {
    const globalObj = window as unknown as { __DIAGRAM_ID__?: string }
    const diagramId = globalObj.__DIAGRAM_ID__ ?? ''
    const m = window.location.pathname.match(/\/f\/([^/]+)\/d\/([^/]+)/)
    const folderId = m ? m[1] : null
    return { diagramId, folderId }
  }

  const onCreateSubProcess = async () => {
    if (!menu.target || menu.target.kind !== 'shape') { menu.close(); return }
    const shapeId = menu.target.shapeId
    const store = useDiagramStore.getState()
    const node = store.nodes.find((n) => n.id === shapeId)
    if (!node) { menu.close(); return }

    const { diagramId, folderId } = getCurrentDiagramInfo()
    if (!diagramId) { menu.close(); return }

    // Get shape label for naming the new diagram
    const attrs = (node as any).attrs || {}
    const shapeLabel = attrs.label || attrs.text || node.text || 'Sub-Process'
    const newDiagramName = String(shapeLabel).trim() || 'Sub-Process'

    // Extract parent shape's data binding context for repository tree linking
    const dataBinding = attrs.dataBinding as { sourceId?: string; recordId?: string } | undefined
    const parentRecordId = dataBinding?.recordId
    // Use shape-level sourceId if bound, otherwise fall back to diagram-level repository
    const parentSourceId = dataBinding?.sourceId || diagramSourceId

    setIsCreatingSubProcess(true)
    try {
      const token = getCookie('gravity_token')
      if (!token) throw new Error('Not authenticated')

      // Create new diagram with same type as current
      const type = diagramType || 'flowchart'
      const queryParams = new URLSearchParams({ name: newDiagramName, type })
      if (folderId) queryParams.set('folderId', folderId)

      const resp = await fetch(`/api/diagrams/new?${queryParams.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      })

      if (!resp.ok) {
        console.error('Failed to create diagram:', resp.status)
        return
      }

      const data = await resp.json()
      const newDiagramId = String(data?.diagramId || '').trim()
      if (!newDiagramId) {
        console.error('Missing diagramId from create response')
        return
      }

      // Build metadata for the link (includes parent binding context if available)
      const linkMetadata: Record<string, unknown> = {}
      if (parentRecordId) linkMetadata.parentRecordId = parentRecordId
      if (parentSourceId) linkMetadata.parentSourceId = parentSourceId

      // Link the new diagram to the shape
      const cm = getCommandManager()
      await cm.execute(new LinkDiagramCommand({
        sourceDiagramId: diagramId,
        sourceShapeId: shapeId,
        targetDiagramId: newDiagramId,
        targetDiagramName: newDiagramName,
        linkType: 'subprocess',
        metadata: Object.keys(linkMetadata).length > 0 ? linkMetadata : undefined,
      }))

      // Connect the child diagram to the same data source
      if (parentSourceId) {
        try {
          await fetch(`/api/diagrams/${newDiagramId}/data-source-connections`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ dataSourceId: parentSourceId }),
            credentials: 'include',
          })
        } catch (err) {
          console.error('Failed to connect data source to child diagram:', err)
        }
      }

      // Open the new sub-process diagram in a new tab
      if (folderId) {
        window.open(`/f/${folderId}/d/${newDiagramId}`, '_blank')
      }
    } catch (error) {
      console.error('Failed to create sub-process:', error)
    } finally {
      setIsCreatingSubProcess(false)
      menu.close()
    }
  }

  const onOpenSubProcess = () => {
    if (!menu.target || menu.target.kind !== 'shape') { menu.close(); return }
    const shapeId = menu.target.shapeId
    const store = useDiagramStore.getState()
    const node = store.nodes.find((n) => n.id === shapeId)
    if (!node) { menu.close(); return }

    const attrs = (node as any).attrs || {}
    const linkedDiagramId = attrs.linkedDiagramId
    if (!linkedDiagramId) { menu.close(); return }

    // Get folder ID from current URL or use a default navigation
    const { folderId } = getCurrentDiagramInfo()
    if (folderId) {
      window.location.href = `/f/${folderId}/d/${linkedDiagramId}`
    } else {
      // Fallback: emit event for navigation
      window.dispatchEvent(new CustomEvent('canvas:link:click', {
        detail: { nodeId: menu.target.shapeId, linkedDiagramId },
      }))
    }
    menu.close()
  }

  const onRemoveSubProcessLink = async () => {
    if (!menu.target || menu.target.kind !== 'shape') { menu.close(); return }
    const shapeId = menu.target.shapeId
    const store = useDiagramStore.getState()
    const node = store.nodes.find((n) => n.id === shapeId)
    if (!node) { menu.close(); return }

    const { diagramId } = getCurrentDiagramInfo()
    if (!diagramId) { menu.close(); return }

    const attrs = (node as any).attrs || {}
    const linkId = attrs.linkId
    if (!linkId) { menu.close(); return }

    try {
      const cm = getCommandManager()
      await cm.execute(new UnlinkDiagramCommand({
        sourceDiagramId: diagramId,
        sourceShapeId: shapeId,
        linkId: String(linkId),
      }))
    } catch (error) {
      console.error('Failed to remove link:', error)
    } finally {
      menu.close()
    }
  }

  const onSaveAsTemplate = () => {
    handleOpenSaveDialog()
    menu.close()
  }

  const onZIndex = async (mode: 'front' | 'forward' | 'backward' | 'back') => {
    try {
      const store = useDiagramStore.getState()
      const selectedIds = store.selectedIds
      const ids = selectedIds.size > 1
        ? Array.from(selectedIds)
        : (menu.target && menu.target.kind === 'shape' ? [menu.target.shapeId] : [])
      if (!ids.length) { menu.close(); return }
      const cm = getCommandManager()
      await cm.execute(new AdjustZIndexCommand(ids, mode))
    } finally {
      menu.close()
    }
  }

  const handleSaveTemplate = (name: string, category: 'strategy' | 'agile' | 'planning' | 'analysis' | 'custom') => {
    if (!tableShapeIdForTemplate) return
    const store = useDiagramStore.getState()
    const node = store.nodes.find((n) => n.id === tableShapeIdForTemplate)
    if (!node || node.type !== 'table') return

    const schemaId = `custom-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
    const extracted = extractSchemaFromTableNode(node, schemaId, name, category)
    if (extracted) {
      const saved = saveCustomSchema(extracted)
      setSchemaToExport(saved)
      const evt = new CustomEvent('table-template-saved', { detail: { schemaId, name } })
      window.dispatchEvent(evt)
    }
  }

  const handleOpenSaveDialog = () => {
    const t = menu.target
    if (!t || t.kind !== 'shape') return
    const store = useDiagramStore.getState()
    setTableShapeIdForTemplate(t.shapeId)
    const node = store.nodes.find((n) => n.id === t.shapeId)
    if (!node || node.type !== 'table') return

    const attrs = ((node as any).attrs ?? {}) as Record<string, unknown>
    const existingSchemaId = typeof attrs.schemaId === 'string' ? attrs.schemaId : null
    const existingSchema = existingSchemaId ? getCustomSchema(existingSchemaId) : null
    setSchemaToExport(existingSchema)
    setSaveTemplateDialogOpen(true)
  }

  const target = menu.target
  const isCanvas = !target || target.kind === 'canvas'
  const shapeTarget = target && target.kind === 'shape' ? target : null
  const isShape = !!shapeTarget
  const isTable = !!shapeTarget && (() => {
    const store = useDiagramStore.getState()
    const node = store.nodes.find((n) => n.id === shapeTarget.shapeId)
    return node?.type === 'table'
  })()

  const contextTargetIsImage = !!shapeTarget && (() => {
    const store = useDiagramStore.getState()
    const node = store.nodes.find((n) => n.id === shapeTarget.shapeId)
    return node?.type === 'image'
  })()

  // Check if shape supports sub-processes and if it has one linked
  const processShapeInfo = (() => {
    if (!shapeTarget) return { isProcess: false, hasLink: false, linkedName: '' }
    const store = useDiagramStore.getState()
    const node = store.nodes.find((n) => n.id === shapeTarget.shapeId)
    if (!node) return { isProcess: false, hasLink: false, linkedName: '' }
    const attrs = (node as any).attrs || {}
    const hasLink = !!(attrs.linkedDiagramId && attrs.linkId)
    const linkedName = attrs.linkedDiagramName || 'Sub-Process'
    return { isProcess: isProcessShape(node), hasLink, linkedName }
  })()
  const canCreateSubProcess = processShapeInfo.isProcess && !processShapeInfo.hasLink
  const canOpenSubProcess = processShapeInfo.isProcess && processShapeInfo.hasLink

  // Check if shape is an unbound entity that can be linked to the data source
  const canLinkToDataSource = (() => {
    if (!shapeTarget || !diagramSourceId) return false
    const store = useDiagramStore.getState()
    const node = store.nodes.find((n) => n.id === shapeTarget.shapeId)
    if (!node) return false
    const attrs = (node as any).attrs || {}
    if (attrs.dataBinding) return false
    const profile = getProfileForDiagramType(diagramType as any)
    if (!profile) return false
    return profile.nodeTypes.includes(node.type ?? '')
  })()

  return (
    <div ref={ref} style={style} className="bg-popover text-popover-foreground border rounded shadow-md min-w-44">
      {isCanvas && (
        <button className="w-full px-3 py-2 hover:bg-muted flex items-center gap-2 text-left" onClick={onPaste}>
          <ClipboardPaste className="w-4 h-4" />
          <span>{t('actions.paste')}</span>
        </button>
      )}
      {isCanvas && diagramType === 'org' && (
        <>
          <div className="h-px bg-border mx-2" />
          <button
            className="w-full px-3 py-2 hover:bg-muted flex items-center gap-2 text-left"
            onClick={() => { setAutoLayoutEnabled(!autoLayoutEnabled); menu.close() }}
          >
            <LayoutGrid className="w-4 h-4" />
            <span>{t('actions.snapToTree', { defaultValue: 'Snap to Tree' })}</span>
            {autoLayoutEnabled && <Check className="w-4 h-4 ml-auto" />}
          </button>
        </>
      )}
      {isShape && (
        <>
          <button className="w-full px-3 py-2 hover:bg-muted flex items-center gap-2 text-left" onClick={onCopy}>
            <CopyIcon className="w-4 h-4" />
            <span>{t('actions.copy')}</span>
          </button>
          {!contextTargetIsImage && (
            <>
              <button className="w-full px-3 py-2 hover:bg-muted flex items-center gap-2 text-left" onClick={onCopyStyle}>
                <Paintbrush className="w-4 h-4" />
                <span>{t('actions.copyStyle')}</span>
              </button>
              <button className="w-full px-3 py-2 hover:bg-muted flex items-center gap-2 text-left" onClick={onPasteStyle}>
                <ClipboardPaste className="w-4 h-4" />
                <span>{t('actions.pasteStyle')}</span>
              </button>
            </>
          )}
          <div className="h-px bg-border mx-2" />
          <button className="w-full px-3 py-2 hover:bg-muted flex items-center gap-2 text-left" onClick={() => onZIndex('front')}>
            <BringToFront className="w-4 h-4" />
            <span>{t('actions.bringToFront')}</span>
          </button>
          <button className="w-full px-3 py-2 hover:bg-muted flex items-center gap-2 text-left" onClick={() => onZIndex('forward')}>
            <MoveUp className="w-4 h-4" />
            <span>{t('actions.bringForward')}</span>
          </button>
          <button className="w-full px-3 py-2 hover:bg-muted flex items-center gap-2 text-left" onClick={() => onZIndex('backward')}>
            <MoveDown className="w-4 h-4" />
            <span>{t('actions.sendBackward')}</span>
          </button>
          <button className="w-full px-3 py-2 hover:bg-muted flex items-center gap-2 text-left" onClick={() => onZIndex('back')}>
            <SendToBack className="w-4 h-4" />
            <span>{t('actions.sendToBack')}</span>
          </button>
        </>
      )}
      {isTable && (
        <>
          <div style={{ display: 'none' }}>
              <div className="h-px bg-border mx-2" />
              <button className="w-full px-3 py-2 hover:bg-muted flex items-center gap-2 text-left" onClick={onSaveAsTemplate}>
                <Save className="w-4 h-4" />
                <span>Save as Template</span>
              </button>
          </div>
        </>
      )}
      {canCreateSubProcess && (
        <>
          <div className="h-px bg-border mx-2" />
          <button
            className="w-full px-3 py-2 hover:bg-muted flex items-center gap-2 text-left disabled:opacity-50"
            onClick={onCreateSubProcess}
            disabled={isCreatingSubProcess}
          >
            <GitBranch className="w-4 h-4" />
            <span>
              {isCreatingSubProcess
                ? t('actions.creatingSubProcess', { defaultValue: 'Creating...' })
                : t('actions.createSubProcess', { defaultValue: 'Create Sub-Process' })}
            </span>
          </button>
        </>
      )}
      {canOpenSubProcess && (
        <>
          <div className="h-px bg-border mx-2" />
          <button className="w-full px-3 py-2 hover:bg-muted flex items-center gap-2 text-left" onClick={onOpenSubProcess}>
            <ExternalLink className="w-4 h-4" />
            <span>
              {t('actions.openLinkedSubProcess', {
                name: processShapeInfo.linkedName,
                defaultValue: 'Open: {{name}}',
              })}
            </span>
          </button>
          <button className="w-full px-3 py-2 hover:bg-muted flex items-center gap-2 text-left text-destructive" onClick={onRemoveSubProcessLink}>
            <Unlink className="w-4 h-4" />
            <span>{t('actions.removeLink', { defaultValue: 'Remove Link' })}</span>
          </button>
        </>
      )}
      {canLinkToDataSource && (
        <>
          <div className="h-px bg-border mx-2" />
          <button
            className="w-full px-3 py-2 hover:bg-muted flex items-center gap-2 text-left"
            onClick={() => {
              if (!shapeTarget || !diagramSourceId || !doc) { menu.close(); return }
              addBindingToShape({
                doc,
                shapeId: shapeTarget.shapeId,
                sourceId: diagramSourceId,
                userId: 'current-user',
              })
              menu.close()
            }}
          >
            <Link2 className="w-4 h-4" />
            <span>Link to Data Source</span>
          </button>
        </>
      )}
      <div className="h-px bg-border mx-2" />
      <button className="w-full px-3 py-2 hover:bg-muted flex items-center gap-2 text-left" onClick={onAddComment}>
        <MessageCirclePlus className="w-4 h-4" />
        <span>{t('comments.addComment')}</span>
      </button>
      <SaveTableTemplateDialog
        open={saveTemplateDialogOpen}
        onOpenChange={(open) => {
          setSaveTemplateDialogOpen(open)
          if (!open) {
            setSchemaToExport(null)
            setTableShapeIdForTemplate(null)
          }
        }}
        onSave={handleSaveTemplate}
        schemaToExport={schemaToExport}
        showExport={!!schemaToExport}
        showImport={true}
      />
    </div>
  )
}
